import React, { useState } from 'react';
import { Input, CustomButton } from '@/components/atomic';
import { forgotPassword as userForgotPassword } from '@/lib/api/user';
import { useToastContext } from '@/components/ui/ToastProvider';
import { useValidation } from '@/hooks/useValidation';

interface ForgotPasswordModalProps {
  onBack?: () => void;
  onSuccess?: (email: string) => void;
  onSwitchToSMS?: () => void;
  forgotPasswordApi?: (email: string) => Promise<any>;
  showSMSOption?: boolean;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onBack, onSuccess, onSwitchToSMS, forgotPasswordApi = userForgotPassword, showSMSOption = true }) => {
  const [email, setEmail] = useState('');
  const { toast } = useToastContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { validateEmail } = useValidation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    setError("Please enter your email address.");
    if (emailError) return;
    setLoading(true);
    try {
      const response = await forgotPasswordApi(email);
      // toast.success(response.message || 'Reset link sent!');
      if (onSuccess) {
        onSuccess(email);
      }
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      if (Array.isArray(apiMessage)) {
        apiMessage.forEach((msg: string) => toast.error(msg));
      } else if (typeof apiMessage === 'string') {
        toast.error(apiMessage);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header with back arrow */}
      <div className="flex items-center mb-6">
        {onBack && (
          <button 
            onClick={onBack}
            className="mr-3 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <h2 className="font-rakesly text-xl font-bold text-gray-800 tracking-wide">
          FORGOT PASSWORD?
        </h2>
      </div>

      {/* Description */}
      <p className="text-gray-600 font-sans text-sm mb-6 leading-relaxed">
        Please enter the email used to create your account
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email*"
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your registered email"
          className="w-full py-3"
          error={error}
        />

        <p className="text-gray-500 font-sans text-xs">
          We'll send you an email with the reset link
        </p>

        {showSMSOption && (
          <div className="text-left">
            <button 
              type="button"
              onClick={onSwitchToSMS}
              className="text-sm cursor-pointer text-gray-800 hover:text-gray-600 transition-colors font-sans underline"
            >
              Don't remember your email? Get SMS
            </button>
          </div>
        )} 

        <div className="pt-4">
          <CustomButton 
            type="submit"
            className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg font-sans font-medium hover:bg-gray-700 transition-all duration-200"
            disabled={loading}
          >
            {loading ? 'Requesting...' : 'Request Password Reset'}
          </CustomButton>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordModal; 